import { useState, useEffect } from "react";
import api from "../Api";

export function useUserDetail(id, auth) {
    const [user, setUser] = useState(null);
    const [editableUser, setEditableUser] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isOwner, setIsOwner] = useState(false);
    const [expires, setExpiresAt] = useState("");
    const [documentCategory, setDocumentCategory] = useState("");
    const [previewImage, setPreviewImage] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [projects, setProjects] = useState([]);
    const [file, setFile] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showUploadForm, setShowUploadForm] = useState(false);
    const [selectedDocId, setSelectedDocId] = useState(null);
    const [editingDocId, setEditingDocId] = useState(null);
    const [editedExpiry, setEditedExpiry] = useState("");
    const [editedCategory, setEditedCategory] = useState("");
    const baseUrl = (window.env && window.env.REACT_APP_BASE_URL) || process.env.REACT_APP_BASE_URL || "http://localhost:8080";

    useEffect(() => {
        fetchUserDetails();
        setIsOwner(auth?.role === "eigenaar");
        // eslint-disable-next-line
    }, [id]);

    const fetchUserDetails = async () => {
        try {
            const response = await api.get(`/users/${id}`);
            if (response.data.profile_picture) {
                setPreviewImage(`${response.data.profile_picture}`);
            }
            await fetchUserDocuments();
            await fetchUserProjects();
            setUser(response.data);
            setEditableUser(response.data);
        } catch (err) {
            setError("Er is iets misgegaan met het laden van de gebruiker.");
        } finally {
            setLoading(false);
        }
    };

    const fetchUserDocuments = async () => {
        try {
            const response = await api.get(`/users/${id}/documents`);
            setDocuments(response.data);
        } catch (err) {
            setError("Fout bij laden van documenten.");
        }
    };

    const fetchUserProjects = async () => {
        try {
            const response = await api.get(`/users/${id}/projects`);
            setProjects(response.data || []);
        } catch (err) {
            console.error("Fout bij laden van projecten:", err);
            setProjects([]);
        }
    };

    const handleDocumentUpload = async (formData, expires, documentCategory) => {
        if (expires) formData.append("expires_at", new Date(expires).toISOString());
        if (documentCategory) formData.append("document_category", documentCategory);
        await api.post(`/users/${id}/documents/upload`, formData);
        await fetchUserDocuments();
    };

    const handleDeleteUserDocument = async (selectedDocId) => {
        await api.delete(`/users/${id}/documents/${selectedDocId}`);
        setDocuments((prev) => prev.filter((doc) => doc.id !== selectedDocId));
    };

    const handleSaveClick = async (editableUser) => {
        await api.put(`/users/${id}/profile`, editableUser);
        setUser(editableUser);
        setIsEditing(false);
    };

    const handleEditExpiry = (doc) => {
        setEditingDocId(doc.id);
        setEditedExpiry(doc.expires_at ? new Date(doc.expires_at).toISOString().split('T')[0] : "");
        setEditedCategory(doc.document_category || "");
    };

    const handleSaveExpiry = async (docId) => {
        await api.put(`/users/${id}/documents/${docId}/expiry`, {
            expires_at: editedExpiry ? new Date(editedExpiry).toISOString() : null,
            document_category: editedCategory
        });
        setEditingDocId(null);
        setEditedExpiry("");
        setEditedCategory("");
        await fetchUserDocuments();
    };

    const handleCancelEditExpiry = () => {
        setEditingDocId(null);
        setEditedExpiry("");
        setEditedCategory("");
    };

    return {
        user,
        editableUser,
        setEditableUser,
        isEditing,
        setIsEditing,
        loading,
        error,
        isOwner,
        expires,
        setExpiresAt,
        documentCategory,
        setDocumentCategory,
        previewImage,
        documents,
        setDocuments,
        projects,
        file,
        setFile,
        showDeleteModal,
        setShowDeleteModal,
        showUploadForm,
        setShowUploadForm,
        selectedDocId,
        setSelectedDocId,
        baseUrl,
        fetchUserDetails,
        fetchUserDocuments,
        handleDocumentUpload,
        handleDeleteUserDocument,
        handleSaveClick,
        editingDocId,
        editedExpiry,
        setEditedExpiry,
        editedCategory,
        setEditedCategory,
        handleEditExpiry,
        handleSaveExpiry,
        handleCancelEditExpiry
    };
}
